import { useState } from "react";
import logo from "../../../assets/images/Logo.png";
import { NavLink } from "react-router-dom";
import {
  ChevronDown,
  Mail,
  Ghost,
  Home,
  Menu,
  ShoppingBag,
  Wrench,
  X,
} from "lucide-react";

const servicesDropdown = [{ label: "Thiết kế website", path: "/services/website-design" }];

const menuItems = [
  { path: "/", label: "Trang chủ", icon: Home, end: true },
  { path: "/about", label: "Giới thiệu", icon: Ghost },
  {
    path: "/store",
    label: "Kho giao diện",
    icon: ShoppingBag,
  },
  {
    path: "/services",
    label: "Dịch vụ",
    icon: Wrench,
    dropdown: servicesDropdown,
  },
  { path: "/contact", label: "Liên hệ", icon: Mail },
];

type BrandProps = {
  compact?: boolean;
};

const BrandMark: React.FC<BrandProps> = ({ compact = false }) => {
  return (
    <div className={`flex items-center ${compact ? "gap-3" : "gap-3.5"}`}>
      <img
        className={
          compact
            ? "h-[42px] w-[42px] rounded-full object-cover"
            : "h-[46px] w-[46px] rounded-full object-cover xl:h-[52px] xl:w-[52px]"
        }
        src={logo}
        alt="TrioCore Solutions"
        width="46"
        height="46"
        loading="eager"
      />

      <div className="flex min-w-0 flex-col leading-none">
        <div
          className={`flex items-end gap-0.5 font-bold ${
            compact ? "text-[18px]" : "text-[18px] sm:text-[19px] xl:text-[23px]"
          }`}
        >
          <span className="text-[#343434]">Trio</span>
          <span className="text-[#2ad4e8]">Core</span>
          <span className={`text-[#343434] ${compact ? "ml-1" : "ml-1.25"}`}>
            Solutions
          </span>
        </div>

        <span
          className={`mt-1 font-medium tracking-[0.1em] text-[#666666] ${
            compact ? "text-[9px]" : "hidden min-[400px]:block text-[9px] sm:text-[10px] xl:text-[11px]"
          }`}
        >
          People. Process. Progress.
        </span>
      </div>
    </div>
  );
};

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const handleMenuClick = () => {
    setMobileMenuOpen(false);
    setMobileDropdown(null);
    scrollToTop();
  };

  return (
    <header className="sticky top-0 z-[1000] w-full border-b border-gray-100 bg-white/95 shadow-[0_4px_20px_rgba(0,0,0,0.08)] backdrop-blur-md">
      <div className="mx-auto flex h-[78px] w-full items-center justify-between px-4 lg:grid lg:h-[88px] lg:max-w-[1440px] lg:grid-cols-[minmax(280px,330px)_1fr_minmax(210px,240px)] lg:items-center lg:gap-6 lg:px-6">
        <div className="min-w-0">
          <BrandMark />
        </div>

        <nav className="hidden min-w-0 lg:flex lg:justify-center">
          <ul className="flex items-center justify-center gap-2 xl:gap-4 2xl:gap-6">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const hasDropdown = !!item.dropdown;

              return (
                <li key={item.path} className="group relative">
                  <NavLink
                    to={item.path}
                    end={item.end as boolean | undefined}
                    onClick={scrollToTop}
                    className={({ isActive }) =>
                      `relative flex items-center gap-2 rounded-md px-3 py-2 text-[15px] font-medium transition-all duration-300 xl:px-4 ${
                        isActive
                          ? "text-[var(--textBlue57)] before:scale-100 before:opacity-100"
                          : "text-gray-600 hover:text-[var(--textBlue57)]"
                      } before:absolute before:inset-0 before:content-[''] before:rounded-md before:bg-gradient-to-r before:from-red-500/15 before:to-rose-700/15 before:opacity-0 before:scale-0 before:transition-all before:duration-500`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <Icon
                            size={16}
                            strokeWidth={3}
                            className="relative z-20 text-[var(--textBlue57)]"
                          />
                        )}
                        <span className="relative z-20 whitespace-nowrap">{item.label}</span>
                      </>
                    )}
                  </NavLink>

                  {hasDropdown && (
                    <div className="invisible absolute left-0 top-full z-50 mt-2 w-56 rounded-lg border border-gray-100 bg-white py-2 opacity-0 shadow-lg transition-all duration-300 group-hover:visible group-hover:opacity-100">
                      {item.dropdown?.map((subItem) => (
                        <NavLink
                          key={subItem.path}
                          to={subItem.path}
                          onClick={scrollToTop}
                          className={({ isActive }) =>
                            `block px-4 py-2 text-left text-sm transition-colors duration-200 ${
                              isActive
                                ? "text-[var(--textBlue57)]"
                                : "text-gray-600 hover:bg-gray-50 hover:text-[var(--textBlue57)]"
                            }`
                          }
                        >
                          {subItem.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="hidden lg:flex lg:justify-end">
          <button className="button-primary h-[40px] min-w-[210px] px-5 font-semibold">
            Hotline 0822041909
          </button>
        </div>

        <button
          onClick={() => setMobileMenuOpen(true)}
          className="p-2 text-gray-600 transition-colors hover:text-[var(--textBlue57)] lg:hidden"
        >
          <Menu size={28} strokeWidth={3} />
        </button>
      </div>

      {/* Mobile Drawer */}
      <div
        className={`fixed inset-0 z-[1001] transition-all duration-300 ${
          mobileMenuOpen ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => {
            setMobileMenuOpen(false);
            setMobileDropdown(null);
          }}
        />
        {/* Drawer Panel */}
        <div
          className={`absolute right-0 top-0 bottom-0 w-[320px] bg-white p-6 shadow-2xl transition-transform duration-300 flex flex-col ${
            mobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-6">
            <BrandMark compact />
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileDropdown(null);
              }}
              className="p-1 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            <ul className="flex flex-col gap-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const hasDropdown = !!item.dropdown;
            const isDropdownOpen = mobileDropdown === item.path;

            return (
              <li key={item.path}>
                {hasDropdown ? (
                  <>
                    <div
                      onClick={() => setMobileDropdown(isDropdownOpen ? null : item.path)}
                      className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg px-4 py-3 font-medium transition-all duration-300 ${
                        isDropdownOpen
                          ? "bg-gradient-to-r from-red-500/15 to-rose-700/15 text-[var(--textBlue57)]"
                          : "text-gray-600 hover:bg-gray-50 hover:text-[var(--textBlue57)]"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon
                          size={20}
                          strokeWidth={isDropdownOpen ? 3 : 2}
                          className={isDropdownOpen ? "text-[var(--textBlue57)]" : "text-gray-500"}
                        />
                        <span>{item.label}</span>
                      </div>
                      <ChevronDown
                        size={16}
                        strokeWidth={3}
                        className={`transition-transform duration-300 ${
                          isDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>

                    {isDropdownOpen && (
                      <div className="ml-4 mt-1 flex flex-col gap-1">
                        {item.dropdown?.map((subItem) => (
                          <NavLink
                            key={subItem.path}
                            to={subItem.path}
                            onClick={handleMenuClick}
                            className={({ isActive }) =>
                              `rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 ${
                                isActive
                                  ? "bg-gradient-to-r from-red-500/10 to-rose-700/10 text-[var(--textBlue57)]"
                                  : "text-gray-600 hover:bg-gray-50 hover:text-[var(--textBlue57)]"
                              }`
                            }
                          >
                            {subItem.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <NavLink
                    to={item.path}
                    end={item.end as boolean | undefined}
                    onClick={handleMenuClick}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-4 py-3 font-medium transition-all duration-300 ${
                        isActive
                          ? "bg-gradient-to-r from-red-500/15 to-rose-700/15 text-[var(--textBlue57)]"
                          : "text-gray-600 hover:bg-gray-50 hover:text-[var(--textBlue57)]"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon
                          size={20}
                          strokeWidth={isActive ? 3 : 2}
                          className={isActive ? "text-[var(--textBlue57)]" : "text-gray-500"}
                        />
                        <span>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                )}
              </li>
            );
          })}
            </ul>

            <div className="mt-6 border-t border-gray-200 pt-6">
              <button className="button-primary h-[42px] w-full font-semibold">
                Hotline 0822041909
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
